/**
 * Landing step of the unified import flow — explains the three-stage import
 * process before the user picks what their spreadsheet contains. Shared by any
 * purchased- or sold-goods import (food, packaging or a mix);
 * content-type-agnostic and reportType-aware (navigation targets resolved via
 * `getGoodsImportRoutes`).
 */
import { useImport } from '@app/components/imports/import-layout';
import { getGoodsImportRoutes } from '@app/components/imports/goods-import/goods-import-routes';
import Page from '@app/components/layout/page/page';
import { useIntercom } from '@app/services/intercom/IntercomContext';
import { OperationReportTypeEnum } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import TextLink from '@shared/components/content/text-link';
import GuideTemplate from '@shared/components/guide-template';
import { useNavigate } from 'react-router-dom';

export default function GoodsImportIntroPage() {
  const { reportType } = useImport();
  const { showIntercomArticle } = useIntercom();
  const navigate = useNavigate();

  const routes = getGoodsImportRoutes(reportType);
  const isSales = reportType === OperationReportTypeEnum.sales;
  const goToNextPage = () => navigate(routes.types);

  return (
    <Page name="Import - Process">
      <GuideTemplate
        preTitle={`Import ${isSales ? 'sales' : 'purchases'}`}
        title="The import process"
        description={
          <div className="space-y-4">
            <Text>
              Our import tool is designed to make the process as simple as
              possible for you. We highly recommend reading our{' '}
              <TextLink onClick={() => showIntercomArticle('foodImport')}>
                data guide
              </TextLink>{' '}
              before starting for the first time.
            </Text>
          </div>
        }
        primaryActionProps={{
          onClick: goToNextPage,
          'data-testid': 'ready-to-import-button',
        }}
        primaryActionText="I'm ready to import"
      >
        <div className="flex space-x-1 py-12">
          <div className="flex flex-1 flex-col items-center rounded-lg border border-teal-200 bg-gray-00 px-4 py-6 shadow-lg">
            <div className="mb-3 flex items-center space-x-2">
              <Icons.upload className="size-5 text-secondary-500" />
              <Text className="text-md font-medium">
                Upload your spreadsheet
              </Text>
            </div>
            <Text>
              Take an export of your {isSales ? 'sales' : 'purchase'} data, tell
              us whether it contains food, packaging or other items, then select
              the relevant columns.
            </Text>
          </div>
          <div className="flex items-center justify-center">
            <Icons.chevronRight className="size-8 text-teal-500" />
          </div>
          <div className="flex flex-1 flex-col items-center rounded-lg border border-teal-200 bg-gray-00 px-4 py-6 shadow-lg">
            <div className="mb-3 flex items-center space-x-2">
              <Icons.coffee className="size-5 text-secondary-500" />
              <Text className="text-md font-medium">
                Let us process your data
              </Text>
            </div>
            <Text>
              Our AI-powered import tool will analyse each row, extracting
              units, matching food and packaging items, and estimating weights
              where necessary.
            </Text>
          </div>
          <div className="flex items-center justify-center">
            <Icons.chevronRight className="size-8 text-teal-500" />
          </div>
          <div className="flex flex-1 flex-col items-center rounded-lg border border-teal-200 bg-gray-00 px-4 py-6 shadow-lg">
            <div className="mb-3 flex items-center space-x-2">
              <Icons.check className="size-5 text-secondary-500" />
              <Text className="text-md font-medium">Review excluded items</Text>
            </div>
            <Text>
              Some items may be excluded from assessment as we can&apos;t
              identify them or estimate their weight. You can review your{' '}
              <TextLink to={routes.excludedItems}>excluded items</TextLink>{' '}
              after import.
            </Text>
          </div>
        </div>
      </GuideTemplate>
    </Page>
  );
}
